/**
 * Helper functions for maintenance job-to-equipment linking
 * Extracted from PlannedMaintenanceTab.js to keep file size manageable
 */

/**
 * FT-186: Get job display name from various job object formats
 * This is the CANONICAL function for getting a job's display name.
 * Uses priority: name > equipmentType > item > fallback
 *
 * @param {Object} job - Maintenance job object
 * @returns {string} Display name (preserves original case)
 */
export function getJobDisplayName(job) {
  if (!job) return 'Maintenance Item';
  return job.name || job.equipmentType || job.item || 'Maintenance Item';
}

/**
 * Get normalized job title from various job object formats
 * @param {Object} job - Maintenance job object
 * @returns {string} Lowercase job title
 */
export function getJobTitle(job) {
  return (job.item || job.equipmentType || job.checkType || job.name || '').toLowerCase();
}

/**
 * Infer equipment type from job title
 * @param {string} title - Lowercase job title
 * @returns {string|null} Equipment type code or null
 */
export function inferJobEquipType(title) {
  if (title.includes('extinguisher')) return 'extinguisher';

  // DETECTORS: Differentiate between fire, CO, and gas detectors
  // Must check specific types BEFORE generic "detector"
  if (title.includes('gas detection') || title.includes('hydrocarbon')) return 'gas';
  if (title.includes('co detection') || title.includes('carbon monoxide')) return 'gas'; // CO detectors also have type "gas"
  if (title.includes('fire detection') || title.includes('smoke detector')) return 'detector';
  if (title.includes('detector') || title.includes('detection')) return 'detector'; // Generic fallback

  if (title.includes('fixed') && title.includes('system')) return 'fixedSystem';
  if (title.includes('pump')) return 'pump';
  if (title.includes('hose')) return 'hose';
  if (title.includes('fire control plan') || title.includes('control plan')) return 'document';
  return null;
}

/**
 * Infer location from job title
 * @param {string} title - Lowercase job title
 * @returns {string|null} Location code or null
 */
export function inferJobLocation(title) {
  // Extract location hints from job title
  if (title.includes('galley') || title.includes('cooking')) return 'galley';
  if (title.includes('engine') || title.includes('machinery')) return 'engine';
  if (title.includes('accommodation') || title.includes('cabin')) return 'accommodation';
  if (title.includes('bridge') || title.includes('wheelhouse') || title.includes('control position')) return 'bridge';
  return null;
}
